"use client";

import { cn } from "@/lib/utils";

export type SeparatorVariant = "single-wave" | "wave-curve" | "layered-wave";
export type SeparatorPosition = "top" | "bottom";

interface SectionSeparatorProps {
  position?: SeparatorPosition;
  variant?: SeparatorVariant;
  className?: string;
  fillClassName?: string;
}

export function SectionSeparator({
  position = "bottom",
  variant = "single-wave",
  className,
  fillClassName,
}: SectionSeparatorProps) {
  const isTop = position === "top";

  const paths = getPaths(variant);
  const viewBox = getViewBox(variant);

  return (
    <div
      className={cn(
        "w-full overflow-hidden leading-0 pointer-events-none relative",
        className,
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={viewBox}
        preserveAspectRatio="none"
      >
        {paths.map((d, index) => (
          <path
            key={index}
            d={d}
            className={cn(
              "fill-(--bg-card)", // Default fill
              fillClassName,
              // For layered wave specific opacities
              variant === "layered-wave" && index === 0 && "opacity-30",
              variant === "layered-wave" && index === 1 && "opacity-50",
              variant === "layered-wave" && index === 2 && "opacity-100",
            )}
          />
        ))}
      </svg>
    </div>
  );
}

function getViewBox(variant: SeparatorVariant): string {
  switch (variant) {
    case "single-wave":
      return "0 0 1000 100";
    case "wave-curve":
      return "0 0 1000 100";
    case "layered-wave":
      return "0 0 1000 170";
    default:
      return "0 0 1000 100";
  }
}

function getPaths(variant: SeparatorVariant): string[] {
  switch (variant) {
    // "Wave Before" (M421)
    case "single-wave":
      return [
        "M421.9,6.5c22.6-2.5,51.5,0.4,75.5,5.3c23.6,4.9,70.9,23.5,100.5,35.7c75.8,32.2,133.7,44.5,192.6,49.7c23.6,2.1,48.7,3.5,103.4-2.5c54.7-6,106.2-25.6,106.2-25.6V0H0v30.3c0,0,72,32.6,158.4,30.5c39.2-0.7,92.8-6.7,134-22.4c21.2-8.1,52.2-18.2,79.7-24.2C399.3,7.9,411.6,7.5,421.9,6.5z",
      ];

    // "Wave After" (M766)
    case "wave-curve":
      return [
        "M766.1,28.9c-200-57.5-266,65.5-395.1,19.5C242,1.8,242,5.4,184.8,20.6C128,35.8,132.3,44.9,89.9,52.5C28.6,63.7,0,0,0,0 h1000c0,0-9.9,40.9-83.6,48.1S829.6,47,766.1,28.9z",
      ];

    // "Banner Wave" (3 Layers)
    case "layered-wave":
      return [
        "M1000,169.4l2-166.45c-155-21.48-310,80.54-415,97.84-102.64,16.91-149-19.09-232-18.49-80,.6-142,31.62-229,47.73-65.54,12.13-101,8.95-126,6.93v32.45h1000Z",
        "M1000,161.65l2-150.34c-157-25.65-302,85.91-405,106.19-101.11,19.91-159-28.04-242-27.44-80,.6-145.09,32.26-229,51.9-65.21,15.27-104.07,9.97-126,6.33v13.36h1000Z",
        "M1000,170l1-137.39c-217-7.58-300.47,77.05-404,93.24-103,16.11-174-17.9-257-17.3-80,.6-130.09,22.12-214,41.76-61.23,14.32-108,9.31-126,6.33v13.36h1000Z",
      ];

    default:
      return [];
  }
}
